import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import ModalWrapper from '@components/wrapper/Modal/ModalWrapper';
import Button from '@components/dataEntry/button/Button';
import Avatar from '@components/dataDisplay/Avatar';
import { ModalHeader, ModalInner } from '@components/wrapper/Modal/ModalStyle';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import { useDocument, useCollection } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import {
  ExplorationArea,
  DispatchOption,
} from '@typings/worldmap/explorationArea.interface';
import {
  calculateRemainingTime,
  formatRemainingTime,
  completeDispatch,
} from '@store/firebase/dispatch/dispatch';
import RewardSection from '@components/dataDisplay/RewardSection';

interface DispatchDetailModalProps {
  isOpen: boolean;
  character: IPhotoCard | null;
  userId: string;
  onClose: () => void;
  onComplete?: () => void;
}

function DispatchDetailModal({
  isOpen,
  character,
  userId,
  onClose,
  onComplete,
}: DispatchDetailModalProps) {
  const [remainingTime, setRemainingTime] = useState<string>('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [isCollecting, setIsCollecting] = useState(false);

  // 파견 지역 정보 가져오기
  const { data: areaData } = useDocument<ExplorationArea>(
    character?.dispatchAreaId
      ? `${CollectionPath.WORLDMAP}/${character.dispatchAreaId}`
      : null,
    { listen: true },
  );

  // 파견 옵션 가져오기 (서브 컬렉션)
  const { data: dispatchOptions } = useCollection<DispatchOption>(
    character?.dispatchAreaId
      ? `${CollectionPath.WORLDMAP}/${character.dispatchAreaId}/dispatchOptions`
      : null,
    { listen: true },
  );

  // 타이머 업데이트
  useEffect(() => {
    if (!character?.dispatchStartedAt || !character?.dispatchDuration) return;

    const updateTimer = () => {
      const remaining = calculateRemainingTime(
        character.dispatchStartedAt!,
        character.dispatchDuration!,
      );
      if (remaining <= 0) {
        setRemainingTime('완료');
        setIsCompleted(true);
      } else {
        setRemainingTime(formatRemainingTime(remaining));
        setIsCompleted(false);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [character?.dispatchStartedAt, character?.dispatchDuration]);

  // 보상 수령
  const handleCollect = useCallback(async () => {
    if (!character?.id) return;

    setIsCollecting(true);
    try {
      const result = await completeDispatch({
        characterId: character.id,
        collectionPath: CollectionPath.PHOTOCARD,
        userId,
      });

      if (result) {
        let message = '파견이 완료되었습니다!';
        if (result.rewards?.investigationText) {
          message += `\n\n📜 ${result.rewards.investigationText.title}`;
        }
        if (result.rewards?.items && result.rewards.items.length > 0) {
          const itemLines = result.rewards.items
            .map((item) => `🎁 ${item.title}${item.count > 1 ? ` x${item.count}` : ''} 획득!`)
            .join('\n');
          message += `\n\n${itemLines}`;
        } else if (result.rewards?.itemTitle) {
          message += `\n\n🎁 ${result.rewards.itemTitle} 획득!`;
        }
        alert(message);
        onComplete?.();
        onClose();
      }
    } catch (error) {
      console.error('보상 수령 실패:', error);
      alert('보상 수령 중 오류가 발생했습니다.');
    } finally {
      setIsCollecting(false);
    }
  }, [character?.id, userId, onComplete, onClose]);

  if (!isOpen || !character) return null;

  const areaName = areaData?.name || '알 수 없는 지역';

  const formatDispatchDuration = (duration: number) => {
    const hours = Math.floor(duration / 60);
    const minutes = Math.floor(duration % 60);
    const seconds = Math.round((duration % 1) * 60);
    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}시간`);
    parts.push(`${minutes}분`);
    if (seconds > 0) parts.push(`${seconds}초`);
    return parts.join(' ');
  };
  // 서브 컬렉션에서 첫 번째 파견 옵션의 보상 아이템 ID 가져오기
  const rewardItemIds = dispatchOptions?.[0]?.rewardItemIds || [];
  const rewardItemCount = dispatchOptions?.[0]?.rewardItemCount ?? rewardItemIds.length;

  return (
    <ModalWrapper outsideClick={onClose} style={{ zIndex: 300 }} contentStyle={{ maxWidth: '550px', width: '100%' }}>
      <ModalHeader>
        <CloseBtn onClick={onClose} />
        <h6>파견 상세 정보</h6>
      </ModalHeader>
      <ModalInner>
        {/* 캐릭터 정보 */}
        <CharacterSection>
          <Avatar photoURL={character.thumbnail || ''} size="80px" />
          <CharacterInfo>
            <CharacterName>{character.title}</CharacterName>
            {character.subTitle && (
              <CharacterSubTitle>{character.subTitle}</CharacterSubTitle>
            )}
          </CharacterInfo>
        </CharacterSection>

        {/* 파견 정보 */}
        <InfoSection>
          <InfoRow>
            <InfoLabel>파견 지역</InfoLabel>
            <InfoValue>{areaName}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>남은 시간</InfoLabel>
            <InfoValue highlight={isCompleted}>
              {isCompleted ? '🎉 완료!' : remainingTime}
            </InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>총 소요 시간</InfoLabel>
            <InfoValue>
              {character.dispatchDuration
                ? formatDispatchDuration(character.dispatchDuration)
                : '-'}
            </InfoValue>
          </InfoRow>
        </InfoSection>

        {/* 보상 정보 */}
        {(rewardItemIds.length > 0 || areaData?.mainRewards) && (
          <RewardsSection>
            <RewardSection
              itemIds={rewardItemIds}
              title={`획득 가능한 랜덤 보상 (${rewardItemCount}개)`}
              showTitle={true}
              titleStyle="divider"
              titleFontSize="12px"
              itemSize={48}
              emptyMessage="보상 정보 없음"
              compact
            />
          </RewardsSection>
        )}

        {/* 버튼 */}
        <ButtonGroup>
          {isCompleted ? (
            <Button
              size="md"
              shape="filled"
              color="primary"
              block
              onClick={handleCollect}
              disabled={isCollecting}
              pending={isCollecting}
            >
              🎁 보상 수령하기
            </Button>
          ) : (
            <Button size="md" shape="stroke" block onClick={onClose}>
              닫기
            </Button>
          )}
        </ButtonGroup>
      </ModalInner>
    </ModalWrapper>
  );
}

export default DispatchDetailModal;

// Styled Components
const CharacterSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: var(--cardBackgroundColor);
  border-radius: var(--cardBorderRadius);
  margin-bottom: 20px;
`;

const CharacterInfo = styled.div`
  flex: 1;
`;

const CharacterName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--textColor);
`;

const CharacterSubTitle = styled.p`
  font-size: 13px;
  color: var(--subTextColor);
  margin: 4px 0 0 0;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--cardBackgroundColor);
  border: 1px solid var(--lineColor);
  border-radius: var(--cardBorderRadius);
  margin-bottom: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: var(--subTextColor);
`;

const InfoValue = styled.span<{ highlight?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ highlight }) =>
    highlight ? 'var(--primaryColor)' : 'var(--textColor)'};
`;

const RewardsSection = styled.div`
  margin-bottom: 20px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--lineColor);
`;
